// Core
import { ReactNode, CSSProperties } from 'react';
// Packages
import { Empty as AntEmpty } from 'antd';
import cx from 'classnames';
// Components
import Icon from '../Icon';
// Styles
import styles from './empty.module.scss';

// The same outline glyph as the mobile placeholders instead of antd's two illustrations; it takes
// the muted text colour of the empty state, so it follows the theme.
const noDataImg = {
  default: <Icon name="inbox" size="xl" />,
  simple: <Icon name="inbox" size="lg" />,
};

interface IEmptyProps {
  className?: string;
  dataCy?: string;
  description?: ReactNode;
  noDataType?: 'default' | 'simple';
  image?: ReactNode;
  imageStyle?: CSSProperties;
  style?: CSSProperties;
}

/**
 * Empty state placeholder.
 *
 * @description When there is no data provided, display for friendly tips.
 * User tutorial to create something in fresh new situation.
 *
 * @param className - Component className
 * @param dataCy - Param for testing purpose
 * @param description - Customize description (antd's localised "No data" when omitted)
 * @param noDataType - Custom prop which defines an image of empty data.
 * @param image - Customize image. Will treat as image url when string provided
 * @param imageStyle - The style of image
 * @param style - To customize the styles
 */

// antd 6 moved `imageStyle` to `styles.image`.
const getEmptyStyles = (image?: CSSProperties) => ({ image });

const EMPTY_CLASS_NAMES = { image: styles.image, description: styles.description };

const Empty = ({
  className,
  dataCy,
  description,
  noDataType = 'default',
  image,
  imageStyle,
  style,
}: IEmptyProps) => (
  <AntEmpty
    className={cx({ [styles.simple]: noDataType === 'simple' }, className)}
    description={description}
    image={image ?? noDataImg[noDataType]}
    classNames={EMPTY_CLASS_NAMES}
    styles={getEmptyStyles(imageStyle)}
    style={style}
    data-cy={dataCy}
  />
);

export default Empty;
